/**
 * Civitas Match Engine v3
 * Implements: Match% = Sum(Wi x (1 - |Ui - Ci|))
 * All values normalized to 0.0-1.0 range
 *
 * CHANGELOG v3:
 *   - Migrated to v3 stance question IDs (aligned with Lovable codebase)
 *   - Added confidence gating: positions with confidence < 0.45 are excluded from scoring
 *   - Removed deprecated __quick_economic and __quick_social quick-match IDs
 *   - Legacy ID migration map included for backwards-compatible localStorage reads
 *   - saveUserAnswers/loadUserAnswers extended to persist state + district
 */

export const CATEGORIES = [
  { id: 'economic_policy',    label: 'Economic Policy' },
  { id: 'taxation',           label: 'Taxation' },
  { id: 'education',          label: 'Education' },
  { id: 'healthcare',         label: 'Healthcare' },
  { id: 'civil_liberties',    label: 'Civil Liberties' },
  { id: 'environment',        label: 'Environment' },
  { id: 'public_safety',      label: 'Public Safety' },
  { id: 'housing',            label: 'Housing' },
  { id: 'foreign_policy',     label: 'Foreign Policy' },
  { id: 'social_welfare',     label: 'Social Welfare' },
  { id: 'governance_reform',  label: 'Governance Reform' },
  { id: 'infrastructure',     label: 'Infrastructure' },
  { id: 'finance_investing',  label: 'Finance & Investing' },
  { id: 'technology_ai',      label: 'Technology/AI Regulation' },
]

export const STANCE_QUESTIONS = [
  { id: 'corporate_subsidies_domestic', label: 'Domestic Corporate Subsidies',        category: 'economic_policy' },
  { id: 'labor_workforce',              label: 'Labor & Workforce Investment',         category: 'economic_policy' },
  { id: 'deregulation',                 label: 'Business Deregulation',                category: 'economic_policy' },
  { id: 'healthcare_delivery',          label: 'Public Healthcare Delivery Model',     category: 'healthcare' },
  { id: 'safety_net',                   label: 'Safety Net Expansion',                 category: 'social_welfare' },
  { id: 'energy_transition',            label: 'Clean Energy Transition',              category: 'environment' },
  { id: 'energy_mix',                   label: 'Nuclear/Gas in Energy Mix',            category: 'environment' },
  { id: 'digital_privacy_ai',           label: 'Digital Privacy & AI Regulation',      category: 'civil_liberties' },
  { id: 'electoral_mechanics',          label: 'Electoral Access & Voting Mechanics',  category: 'governance_reform' },
  { id: 'term_limits',                  label: 'Term Limits for Legislators',          category: 'governance_reform' },
  { id: 'criminal_justice_financing',   label: 'Criminal Justice & Financing Reform',  category: 'public_safety' },
  { id: 'community_policing',           label: 'Community Response vs. Traditional Policing', category: 'public_safety' },
]

export const CONFIDENCE_THRESHOLD = 0.45

export const LEGACY_ID_MAP = {
  corporate_taxation: 'corporate_subsidies_domestic',
  minimum_wage:       'labor_workforce',
  public_option:      'healthcare_delivery',
  carbon_neutrality:  'energy_transition',
  data_privacy:       'digital_privacy_ai',
  voting_access:      'electoral_mechanics',
  sentencing_reform:  'criminal_justice_financing',
  __quick_economic:   null,
  __quick_social:     null,
}

export function normalizeWeight(w) {
  return (Math.max(1, Math.min(10, w)) - 1) / 9
}

export function normalizeStance(s) {
  return (Math.max(-5, Math.min(5, s)) + 5) / 10
}

export function migrateLegacyStances(stances) {
  const migrated = {}
  for (const [key, value] of Object.entries(stances || {})) {
    if (key in LEGACY_ID_MAP) {
      const newKey = LEGACY_ID_MAP[key]
      if (newKey !== null) migrated[newKey] = value
    } else {
      migrated[key] = value
    }
  }
  return migrated
}

/**
 * The scorable vocabulary: the only keys a confidence lookup can ever hit,
 * because the gate is read as confidenceMap[q.id]. Anything else is excluded
 * by construction, not by filtering.
 */
const SCORABLE_TOPICS = new Set(STANCE_QUESTIONS.map(q => q.id))

/**
 * AB#814. Resolve one evidence topic onto the current vocabulary.
 *
 * WHY THIS EXISTS, AND WHY IT IS THIS MAP AND NOT A NEW ONE
 *
 * computeMatch compares two things: the voter stance and the candidate
 * position. Both are keyed by the same vocabulary, and that vocabulary was
 * renamed. The line `userStances = migrateLegacyStances(userStances)` below
 * has been applying this exact map to the VOTER side of that subtraction, in
 * production, since v3. It was never applied to the candidate side.
 *
 * So this is not a new semantic judgement about whether corporate_taxation
 * "means" corporate_subsidies_domestic. The codebase already answered that and
 * ships the answer to voters. This applies the same answer to the other operand.
 *
 * Measured 2026-11-02 against production candidate_evidence (546 rows):
 *   329 rows carry one of the 7 legacy topics  -> renamed here
 *   105 rows carry a current topic             -> returned unchanged
 *   112 rows carry one of 3 topics that have no questionnaire question
 *                                              -> returned unchanged, then
 *                                                 excluded by SCORABLE_TOPICS
 *   329 + 105 + 112 = 546. There is no fourth bucket.
 *
 * DELIBERATELY NOT PERMISSIVE. An unrecognised topic is returned verbatim, so
 * it can only score if it is already exactly a question id. Normalization never
 * coerces an unknown string onto a scoring key.
 *
 * @param {string} topic - raw candidate_evidence.topic
 * @returns {string|null} the resolved key, or null for a deprecated topic with
 *                        no current equivalent (__quick_economic/__quick_social)
 */
export function normalizeEvidenceTopic(topic) {
  if (typeof topic !== 'string' || topic === '') return null
  if (topic in LEGACY_ID_MAP) return LEGACY_ID_MAP[topic]
  return topic
}

export function computeMatch(userWeights, userStances, candidate) {
  const { position_vector, evidence_ledger } = candidate || {}
  userWeights = userWeights || {}
  userStances = migrateLegacyStances(userStances)

  // AB#814: resolve the topic BEFORE keying. The gate below reads this map by
  // q.id, so a legacy key here is not a near miss -- it is a total miss that
  // yields 0 via `?? 0`, trips the confidence gate, and returns the candidate
  // as unscorable while holding real, high-confidence evidence.
  const confidenceMap = {}
  const unmatchedTopics = []
  for (const entry of Array.isArray(evidence_ledger) ? evidence_ledger : []) {
    if (!entry?.topic || !Number.isFinite(entry.confidence_score)) continue
    if (entry.confidence_score < 0 || entry.confidence_score > 1) continue

    const key = normalizeEvidenceTopic(entry.topic)
    if (key === null) continue // deprecated topic with no current equivalent

    if (!SCORABLE_TOPICS.has(key)) {
      // A real dimension with no questionnaire question, or an unrecognised
      // string. Either way it cannot score -- but it must not do so SILENTLY.
      // This bug survived because an evidence topic that matched nothing
      // produced no signal at all, anywhere.
      if (!unmatchedTopics.includes(entry.topic)) unmatchedTopics.push(entry.topic)
      continue
    }

    // MIN across duplicates is unchanged. See the AC5 note in
    // legacyTopicNormalization.test.js: normalization is what can newly collide
    // two distinct legacy topics onto one key, so this rule stops being
    // unexercised the moment this function lands.
    confidenceMap[key] = Math.min(confidenceMap[key] ?? 1, entry.confidence_score)
  }

  let totalWeight = 0
  let weightedSum = 0
  let skippedCount = 0
  let answeredCount = 0
  let scoredCount = 0
  let unknownCount = 0
  const breakdown = {}

  for (const cat of CATEGORIES) {
    const rawWeight = userWeights[cat.id] ?? 5
    const Wi = Number.isFinite(rawWeight) ? normalizeWeight(rawWeight) : 0
    const catQuestions = STANCE_QUESTIONS.filter(q => q.category === cat.id)

    let catScore = null
    if (catQuestions.length > 0) {
      let qSum = 0
      let qCount = 0

      for (const q of catQuestions) {
        const rawStance = userStances[q.id]
        if (!Number.isFinite(rawStance) || rawStance < -5 || rawStance > 5) continue
        answeredCount++

        const confidence = confidenceMap[q.id] ?? 0
        if (confidence < CONFIDENCE_THRESHOLD) {
          skippedCount++
          continue
        }

        // AB#698: a position we do not have is EXCLUDED, not imputed as 0.5.
        // Imputing gave every candidate with no data a fixed score derived from
        // how moderate the voter's own answers were — a mirror of the voter
        // rather than a measurement of the candidate — and it outranked every
        // candidate the system actually had evidence for. Absence of a position
        // is not agreement, and it is not disagreement either.
        const Ci = position_vector?.[q.id]
        if (!Number.isFinite(Ci) || Ci < 0 || Ci > 1) {
          unknownCount++
          continue
        }

        const Ui = normalizeStance(rawStance)
        qSum += 1 - Math.abs(Ui - Ci)
        qCount++
      }

      catScore = qCount > 0 ? qSum / qCount : null
      scoredCount += qCount
    } else {
      // No voter stance exists for this category; do not invent neutrality.
      catScore = null
    }

    // Only categories we could actually score contribute to the weighted mean.
    // Including an unscored category at a neutral value would reintroduce the
    // same imputation one level up.
    if (catScore !== null) {
      weightedSum += Wi * catScore
      totalWeight += Wi
    }
    breakdown[cat.id] = catScore === null ? null : Math.round(catScore * 100)
  }

  // null, not 0. "We have no basis to score this candidate" is a different
  // statement from "this candidate matches you 0%", and a voter must be able to
  // tell them apart. Callers render this as a reason, never as a number.
  const score = totalWeight > 0
    ? Math.round((weightedSum / totalWeight) * 100)
    : null

  return {
    score,
    breakdown,
    skippedCount,
    coverage: {
      answered: answeredCount,   // questions the voter answered
      scored: scoredCount,       // of those, how many we had a position for
      unknown: unknownCount,     // no position on record
      lowConfidence: skippedCount,
      // AB#814: evidence topics that resolved to no scorable question. Empty is
      // the healthy state. Non-empty names exactly which rows were discarded.
      unmatchedTopics,
    },
  }
}

/**
 * The best candidate we could actually score, or null if none could be.
 *
 * AB#698: a consumer reaching for ranked[0] gets an UNSCORABLE candidate when
 * nothing is scorable, and presents it as "your best match" — which is
 * presenting ignorance as a recommendation, the precise thing this card
 * removes. That undoes the null contract one line after the engine establishes
 * it, so the safe accessor belongs here rather than in every caller's memory.
 *
 * @param {Array<object>} ranked - output of rankCandidates
 * @returns {object|null}
 */
export function topScorable(ranked) {
  if (!Array.isArray(ranked)) return null
  return ranked.find(c => c.matchScore !== null && c.matchScore !== undefined) ?? null
}

export function rankCandidates(userWeights, userStances, candidates) {
  const migratedStances = migrateLegacyStances(userStances)
  return candidates
    .map(c => {
      const { score, breakdown, skippedCount, coverage } = computeMatch(userWeights, migratedStances, c)
      return { ...c, matchScore: score, breakdown, skippedCount, coverage }
    })
    // Unscorable candidates sort LAST, and keep a null score rather than a
    // number. `b.matchScore - a.matchScore` with a null coerces to 0 and would
    // silently interleave them among genuine low scorers — which is the bug in
    // a quieter form. AB#698.
    .sort((a, b) => {
      if (a.matchScore === null && b.matchScore === null) return String(a.id).localeCompare(String(b.id))
      if (a.matchScore === null) return 1
      if (b.matchScore === null) return -1
      return b.matchScore - a.matchScore || String(a.id).localeCompare(String(b.id))
    })
}

export function loadUserAnswers() {
  try {
    const weights = JSON.parse(localStorage.getItem('civitas_weights') || '{}')
    const rawStances = JSON.parse(localStorage.getItem('civitas_stances') || '{}')
    const stances = migrateLegacyStances(rawStances)
    const state = localStorage.getItem('civitas_state') || null
    const district = localStorage.getItem('civitas_district') || null
    return { weights, stances, state, district }
  } catch {
    return { weights: {}, stances: {}, state: null, district: null }
  }
}

export function saveUserAnswers(weights, stances, state, district) {
  localStorage.setItem('civitas_weights', JSON.stringify(weights))
  localStorage.setItem('civitas_stances', JSON.stringify(stances))
  if (state !== undefined && state !== null) localStorage.setItem('civitas_state', state)
  if (district !== undefined && district !== null) localStorage.setItem('civitas_district', district)
}

export function hasCompletedQuestionnaire() {
  const { weights, stances } = loadUserAnswers()
  return (
    Object.keys(weights).length >= CATEGORIES.length &&
    Object.keys(stances).length >= STANCE_QUESTIONS.length
  )
}
